/**
 * Budget thresholds — which marks a single write carried a category across.
 *
 * Two marks: 80% (a warning, felt as a haptic and shown in the app) and 100%
 * (the high-water mark). A mark is reported **only by the write that crosses
 * it**, so logging a coffee in a category already at 90% says nothing new.
 *
 * **Integer comparisons only, never a division** (INV-1): `spent × 100 ≥
 * budget × mark`. Dividing first would put a float between a cent and the
 * answer — 80% of $0.33 is 26.4¢, and which side 26¢ falls on must not depend
 * on rounding. The same function is ported to Swift and Kotlin for the native
 * logging path and checked against the same cases.
 */

import { type Cents, cents } from "./money";

export type Mark = 80 | 100;

export const MARKS: readonly Mark[] = [80, 100];

const PERCENT = 100;

function reached(spent: number, budget: number, mark: Mark): boolean {
  return spent * PERCENT >= budget * mark;
}

/** The marks crossed going from `spentBefore` to `spentAfter`, lowest first. Empty without a budget. */
export function crossedMarks(spentBefore: Cents, spentAfter: Cents, budgetCents: Cents | null): Mark[] {
  // `cents()` throws on anything fractional — the brand is a compile-time
  // promise, and this is the runtime one.
  const before = cents(spentBefore);
  const after = cents(spentAfter);
  if (budgetCents === null) return [];
  const budget = cents(budgetCents);
  return MARKS.filter((mark) => !reached(before, budget, mark) && reached(after, budget, mark));
}
